(function () {
  const STORAGE_KEY = 'bata-vat-show-exclusive';

  FoxTheme.VatPricing = {
    get settings() {
      return FoxTheme.settings.vatPricing || {};
    },

    isEnabled() {
      const { enabled, rate } = this.settings;
      return Boolean(enabled) && Number(rate) > 0;
    },

    getShowExclusive() {
      if (!this.isEnabled()) {
        return false;
      }

      let stored = null;
      try {
        stored = localStorage.getItem(STORAGE_KEY);
      } catch (e) {
        // Fallback if localStorage is disabled or restricted
      }
      if (stored !== null) {
        return stored === 'true';
      }

      const toggleControl = document.querySelector('vat-toggle .vat-toggle__control[role="switch"]');
      if (toggleControl) {
        return toggleControl.getAttribute('aria-checked') === 'true';
      }

      return Boolean(this.settings.defaultExclusive);
    },

    setShowExclusive(showExclusive) {
      if (!this.isEnabled()) {
        return;
      }

      try {
        localStorage.setItem(STORAGE_KEY, showExclusive ? 'true' : 'false');
      } catch (e) {
        // Fallback if localStorage is disabled or restricted
      }
      document.documentElement.dataset.vatDisplay = showExclusive ? 'exclusive' : 'inclusive';
      this.updateAll();
      document.dispatchEvent(
        new CustomEvent('vat-pricing:change', {
          detail: { showExclusive },
        })
      );
    },

    getElementRate(element) {
      const source = element?.closest?.('[data-vat-rate]') || element;
      if (!source?.dataset) {
        return Number(this.settings?.rate) || 0;
      }

      // Reduced-rate items keep their price when the inc/ex toggle changes, same as zero-rated.
      if (
        source.dataset.vatTaxable === 'false' ||
        source.dataset.vatStatus === 'zero' ||
        source.dataset.vatStatus === 'reduced'
      ) {
        return 0;
      }

      if (source.dataset.vatRate !== undefined && source.dataset.vatRate !== '') {
        return Number(source.dataset.vatRate) || 0;
      }

      return Number(this.settings?.rate) || 0;
    },

    getVariantRate(variant, context) {
      if (variant && variant.taxable === false) {
        return 0;
      }

      if (context) {
        const source =
          (context.closest && context.closest('[data-vat-rate]')) ||
          (context.querySelector && context.querySelector('[data-vat-rate]'));
        if (source) {
          return this.getElementRate(source);
        }
      }

      if (variant && variant.taxable === true) {
        return Number(this.settings?.rate) || 0;
      }

      return Number(this.settings?.rate) || 0;
    },

    adjustCents(cents, rate) {
      const centsNum = Number(cents);
      if (!this.isEnabled() || Number.isNaN(centsNum)) {
        return centsNum;
      }

      const itemRate = rate === undefined ? Number(this.settings?.rate) || 0 : Number(rate) || 0;
      if (itemRate <= 0) {
        return centsNum;
      }

      const multiplier = 1 + itemRate / 100;
      const showExclusive = this.getShowExclusive();

      if (this.settings.basis === 'inclusive') {
        return showExclusive ? Math.round(centsNum / multiplier) : centsNum;
      }

      return showExclusive ? centsNum : Math.round(centsNum * multiplier);
    },

    toExclusiveCents(cents) {
      const centsNum = Number(cents);
      if (Number.isNaN(centsNum)) {
        return centsNum;
      }

      return centsNum;
    },

    toInclusiveCents(cents, rate) {
      const centsNum = Number(cents);
      if (!this.isEnabled() || Number.isNaN(centsNum)) {
        return centsNum;
      }

      const itemRate = rate === undefined ? Number(this.settings?.rate) || 0 : Number(rate) || 0;
      if (itemRate <= 0) {
        return centsNum;
      }

      return Math.round(centsNum + centsNum * (itemRate / 100));
    },

    formatMoney(cents, rate) {
      return FoxTheme.Currency.formatMoney(this.adjustCents(cents, rate), FoxTheme.settings.moneyFormat);
    },

    formatInclusiveMoney(cents, rate) {
      return FoxTheme.Currency.formatMoney(this.toInclusiveCents(cents, rate), FoxTheme.settings.moneyFormat);
    },

    formatExclusiveMoney(cents) {
      return FoxTheme.Currency.formatMoney(this.toExclusiveCents(cents), FoxTheme.settings.moneyFormat);
    },

    updateElement(element) {
      const rate = this.getElementRate(element);

      if (element.dataset.vatAlwaysExclusive === 'true') {
        const cents = element.dataset.vatCents;
        if (cents === undefined) {
          return;
        }

        element.textContent = this.formatExclusiveMoney(cents);
        return;
      }

      if (element.dataset.vatInverted === 'true') {
        const cents = element.dataset.vatCents;
        if (cents === undefined) {
          return;
        }

        const formatted = this.getShowExclusive()
          ? this.formatInclusiveMoney(cents, rate)
          : this.formatExclusiveMoney(cents);
        element.textContent = formatted;
        return;
      }

      const format = element.dataset.vatFormat || 'money';
      const alwaysInclusive = element.dataset.vatAlwaysInclusive === 'true';

      if (format === 'range') {
        const minimum = alwaysInclusive
          ? this.formatInclusiveMoney(element.dataset.vatCentsMin, rate)
          : this.formatMoney(element.dataset.vatCentsMin, rate);
        const maximum = alwaysInclusive
          ? this.formatInclusiveMoney(element.dataset.vatCentsMax, rate)
          : this.formatMoney(element.dataset.vatCentsMax, rate);
        const template = this.settings.volumeRangeTemplate || '';
        element.textContent = template
          .replace('[minimum]', minimum)
          .replace('[maximum]', maximum);
        return;
      }

      if (format === 'from') {
        const price = alwaysInclusive
          ? this.formatInclusiveMoney(element.dataset.vatCents, rate)
          : this.formatMoney(element.dataset.vatCents, rate);
        const template = this.settings.fromPriceTemplate || '';
        element.innerHTML = template.replace('[price]', price);
        return;
      }

      const cents = element.dataset.vatCents;
      if (cents === undefined) {
        return;
      }

      const formatted = alwaysInclusive
        ? this.formatInclusiveMoney(cents, rate)
        : this.formatMoney(cents, rate);
      if (element.dataset.vatWrapCompare === 'true') {
        element.innerHTML = `<s>${formatted}</s>`;
      } else {
        element.textContent = formatted;
      }
    },

    updateProductVatLabel(root = document) {
      root.querySelectorAll('.product__vat-prices[data-vat-ex-label]').forEach((container) => {
        const label = container.querySelector('.product__price-ex-vat-label');
        if (!label) {
          return;
        }

        const status = container.dataset.vatStatus;
        if (status === 'zero') {
          label.textContent = container.dataset.vatZeroLabel || 'Zero VAT';
          return;
        }

        if (status === 'reduced') {
          label.textContent = container.dataset.vatReducedLabel || 'Reduced VAT';
          return;
        }

        if (this.getShowExclusive()) {
          label.textContent = container.dataset.vatIncLabel || 'Inc. VAT';
        } else {
          label.textContent = container.dataset.vatExLabel || 'Ex. VAT';
        }
      });
    },

    updateAll(root = document) {
      if (!this.isEnabled()) {
        return;
      }

      root.querySelectorAll('[data-vat-cents]').forEach((element) => this.updateElement(element));
      this.updateProductVatLabel(root);
    },

    bindEvents() {
      document.addEventListener('DOMContentLoaded', () => this.init());

      document.addEventListener('vat-pricing:change', () => this.updateAll());

      if (FoxTheme.pubsub?.subscribe && FoxTheme.pubsub?.PUB_SUB_EVENTS?.variantChange) {
        FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, () => {
          requestAnimationFrame(() => this.updateAll());
        });
      }

      document.addEventListener('variant:changed', () => {
        requestAnimationFrame(() => this.updateAll());
      });
    },

    init() {
      if (!this.isEnabled()) {
        return;
      }

      document.documentElement.dataset.vatDisplay = this.getShowExclusive() ? 'exclusive' : 'inclusive';
      this.updateAll();

      document.querySelectorAll('vat-toggle').forEach((toggle) => {
        if (typeof toggle.syncState === 'function') {
          toggle.syncState();
        }
      });
    },
  };

  class VatToggle extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
      this.handleExternalChange = this.handleExternalChange.bind(this);
    }

    connectedCallback() {
      this.control = this.querySelector('.vat-toggle__control[role="switch"]');
      if (!this.control) {
        return;
      }

      if (!FoxTheme.VatPricing.isEnabled()) {
        this.classList.add('hidden');
        return;
      }

      this.control.addEventListener('click', this.handleClick);
      document.addEventListener('vat-pricing:change', this.handleExternalChange);
      this.syncState();
    }

    disconnectedCallback() {
      if (this.control) {
        this.control.removeEventListener('click', this.handleClick);
      }
      document.removeEventListener('vat-pricing:change', this.handleExternalChange);
    }

    handleClick() {
      if (!this.control) {
        return;
      }

      const showExclusive = this.control.getAttribute('aria-checked') !== 'true';
      this.setControlState(showExclusive);
      FoxTheme.VatPricing.setShowExclusive(showExclusive);
    }

    handleExternalChange(event) {
      this.setControlState(Boolean(event.detail.showExclusive));
    }

    setControlState(showExclusive) {
      if (!this.control) {
        return;
      }

      this.control.setAttribute('aria-checked', showExclusive ? 'true' : 'false');
      this.updateLabel(showExclusive);
    }

    updateLabel(showExclusive) {
      const exLabel = this.dataset.vatExLabel || 'Ex. VAT';
      const incLabel = this.dataset.vatIncLabel || 'Inc. VAT';
      const labelText = showExclusive ? exLabel : incLabel;

      const label = this.querySelector('.vat-toggle__label');
      const hiddenLabel = this.querySelector('.vat-toggle__control .visually-hidden');

      if (label) {
        label.textContent = labelText;
      }

      if (hiddenLabel) {
        hiddenLabel.textContent = labelText;
      }
    }

    syncState() {
      if (!this.control || !FoxTheme.VatPricing.isEnabled()) {
        return;
      }

      this.setControlState(FoxTheme.VatPricing.getShowExclusive());
    }
  }

  if (!customElements.get('vat-toggle')) {
    customElements.define('vat-toggle', VatToggle);
  }

  FoxTheme.VatPricing.bindEvents();

  if (document.readyState !== 'loading') {
    FoxTheme.VatPricing.init();
  }
})();
